import React from 'react';
import { format } from 'date-fns';
import signatureImage from '../images/signature.png';

function QuotePreview({
  client,
  clientName,
  billToAddress,
  shipToAddress,
  clientContact,
  quoteNumber,
  quoteDate,
  reference,
  placeOfSupply,
  items,
  totals,
  amountInWords,
  printRef,
  handlePrint,
  config
}) {
  const formatCurrency = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(n || 0));
  
  // Resolve bank details based on client's selected bank, with fallback to first configured bank
  const selectedBank = config?.bankAccounts?.find(bank => bank.id === (client?.bankAccountId));
  const bankDetails = selectedBank || (config?.bankAccounts?.[0] || null);
  
  // Convert place of supply code to full format
  const getPlaceOfSupplyDisplay = (code) => {
    if (!code) return 'N/A';
    
    const placeOfSupplyMap = {
      '01': 'Jammu & Kashmir (01)',
      '02': 'Himachal Pradesh (02)',
      '03': 'Punjab (03)',
      '04': 'Chandigarh (04)',
      '05': 'Uttarakhand (05)',
      '06': 'Haryana (06)',
      '07': 'Delhi (07)',
      '08': 'Rajasthan (08)',
      '09': 'Uttar Pradesh (09)',
      '10': 'Bihar (10)',
      '11': 'Sikkim (11)',
      '12': 'Arunachal Pradesh (12)',
      '13': 'Nagaland (13)',
      '14': 'Manipur (14)',
      '15': 'Mizoram (15)',
      '16': 'Tripura (16)',
      '17': 'Meghalaya (17)',
      '18': 'Assam (18)',
      '19': 'West Bengal (19)',
      '20': 'Jharkhand (20)',
      '21': 'Odisha (21)',
      '22': 'Chhattisgarh (22)',
      '23': 'Madhya Pradesh (23)',
      '24': 'Gujarat (24)',
      '26': 'Dadra and Nagar Haveli and Daman and Diu (26)',
      '27': 'Maharashtra (27)',
      '29': 'Karnataka (29)',
      '30': 'Goa (30)',
      '31': 'Lakshadweep (31)',
      '32': 'Kerala (32)',
      '33': 'Tamil Nadu (33)',
      '34': 'Puducherry (34)',
      '35': 'Andaman & Nicobar Islands (35)',
      '36': 'Telangana (36)',
      '37': 'Andhra Pradesh (New) (37)',
      '38': 'Ladakh (38)',
      '97': 'Other Territory (97)',
      '99': 'Center Jurisdiction (99)'
    };
    
    return placeOfSupplyMap[code] || code;
  };

  return (
    <div className="invoice-preview" ref={printRef} style={{ position: 'relative' }}>
      <img src="/spatium-logo-1.png" alt="Watermark" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '60%', opacity: 0.07, zIndex: 0, pointerEvents: 'none', userSelect: 'none' }} />
      <div className="invoice-header" style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingTop: '20px', paddingBottom: '20px'}}>
        <img src="/spatium-logo-1.png" alt="Spatium Logo" style={{ maxWidth: '160px', height: 'auto', flex: '0 0 auto', padding: '10px'}} />
        <div style={{ textAlign: 'right', flex: 1, paddingRight:"10px" }}>
          <h2 className="invoice-title" style={{ marginBottom: 0 }}>QUOTE</h2>
          <p style={{ marginTop: -1, marginBottom: 0 }}>Spatium Iconic, 4, 4-1, Bannerghatta Main Road,<br />JP Nagar Phase 3, Bengaluru, Karnataka, 560078</p>
          <p style={{ marginTop: 2, marginBottom: 0 }}><strong>GSTIN:</strong> {config?.gstin || 'GSTIN'}</p>
          {config?.udyamNo && (
            <p style={{ marginTop: 2, marginBottom: 0 }}><strong>UDYAM NO:</strong> {config.udyamNo}</p>
          )}
        </div>
      </div>

      <div className="invoice-details" style={{ display: 'flex', gap: '0px' }}>
        <div style={{ flex: '0 0 50%' }}>
          {/* Bill To Box */}
          <div className="invoice-info" style={{ borderBottom: '1px solid #e6ecf1', paddingBottom: '12px' }}>
            <h3>Bill To:</h3>
            <p><strong>{clientName || client?.registeredName || client?.name || 'Client Name'}</strong></p>
            <p>{billToAddress || client?.billToAddress || client?.address || 'Address'}</p>
            <p>{clientContact || client?.phone || 'Phone'}</p>
          </div>
          
          {/* Ship To Box */}
          <div className="invoice-info" style={{ borderBottom: '1px solid #e6ecf1', paddingBottom: '12px' }}>
            <h3>Ship To:</h3>
            <p><strong>{clientName || client?.registeredName || client?.name || 'Client Name'}</strong></p>
            <p>{shipToAddress || billToAddress || client?.shipToAddress || client?.billToAddress || client?.address || 'Address'}</p>
            <p>{clientContact || client?.phone || 'Phone'}</p>
          </div>
          
          {/* GSTIN Box */}
          <div className="invoice-info" style={{ borderBottom: '0px' }}>
            <h3>Client GSTIN:</h3>
            <p>{client?.gstin || 'N/A'}</p>
          </div>
        </div>
        <div style={{ flex: '0 0 50%' }}>
          <table className="invoice-meta-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 400, width: '50%', borderRight: '1px solid #333' }}>Quote No.<br/>
                  <span style={{ fontWeight: 700 }}>{quoteNumber}</span>
                </td>
                <td style={{ fontWeight: 400  }}>Dated<br/><span style={{ fontWeight: 700 }}>{quoteDate ? format(new Date(quoteDate), 'dd-MMM-yy') : ''}</span></td>
              </tr>
              {reference && (
                <tr>
                  <td style={{ fontWeight: 400, borderRight: '1px solid #333' }}>Reference<br/>
                    <span style={{ fontWeight: 700 }}>{reference}</span>
                  </td>
                  <td style={{ fontWeight: 400 }}>Place of Supply<br/><span style={{ fontWeight: 700 }}>{getPlaceOfSupplyDisplay(placeOfSupply)}</span></td>
                </tr>
              )}
              {!reference && placeOfSupply && (
                <tr>
                  <td colSpan="2" style={{ fontWeight: 400 }}>Place of Supply<br/><span style={{ fontWeight: 700 }}>{getPlaceOfSupplyDisplay(placeOfSupply)}</span></td>
                </tr>
              )}
              <tr>
                <td colSpan="2" style={{ fontWeight: 400 }}>Payment Terms<br/><span style={{ fontWeight: 700 }}>100% Advance</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <table className="invoice-table" style={{ width: '100%', tableLayout: 'auto' }}>
        <thead>
          <tr>
            <th style={{ width: '4%', textAlign: 'center' }}>Sr.</th>
            <th style={{ width: '32%' }}>Item & Description</th>
            <th style={{ width: '8%' }}>HSN/SAC</th>
            <th style={{ width: '6%', textAlign: 'right' }}>Qty</th>
            <th style={{ width: '10%', textAlign: 'right' }}>Rate</th>
            <th style={{ width: '12%', textAlign: 'right' }}>Base Amt</th>
            <th style={{ width: '6%', textAlign: 'right' }}>Tax %</th>
            <th style={{ width: '10%', textAlign: 'right' }}>Tax Amt</th>
            <th style={{ width: '12%', textAlign: 'right' }}>Total Amt</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((item, index) => (
            <tr key={index} style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none', textAlign: 'center' }}>{index + 1}</td>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>
                {(item.imageBase64 || item.imageUrl) && (
                  <img 
                    src={(() => {
                      if (item.imageBase64) {
                        return item.imageBase64;
                      }
                      if (item.imageUrl) {
                        if (item.imageUrl.startsWith('http')) {
                          return item.imageUrl;
                        }
                        const base = window.location.hostname === 'ledgerly.spatium360.com'
                          ? 'https://ledgerly.spatium360.com'
                          : 'http://localhost:5050';
                        return `${base}${item.imageUrl}`;
                      }
                      return '';
                    })()} 
                    alt="item" 
                    style={{ 
                      maxWidth: '120px', 
                      maxHeight: '80px', 
                      objectFit: 'contain', 
                      marginBottom: 6, 
                      border: '1px solid #e6ecf1', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px', 
                      padding: '4px' 
                    }} 
                    onError={(e) => {
                      console.error('Image load error for item:', item.description);
                      console.error('Image URL:', e.target.src);
                      console.error('Original imageUrl:', item.imageUrl);
                      console.error('Has imageBase64:', !!item.imageBase64);
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully for item:', item.description);
                    }}
                  />
                )}
                <div><strong>{item.description}</strong></div>
              </td>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none' }}>{item.hsn || ''}</td>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>{item.qty || 0}</td>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatCurrency(item.rate)}</td>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatCurrency(item.baseAmount)}</td>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>{Number(item.taxPercent || 0)}%</td>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatCurrency(item.taxAmount)}</td>
              <td style={{ borderBottom: index < (items || []).length - 1 ? '1px solid #e6ecf1' : 'none', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatCurrency(item.totalAmount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="invoice-total">
        <table>
          <tbody>
            <tr>
              <td style={{borderBottom: '0px', borderRight: '0px' }}>Subtotal:</td>
              <td style={{borderBottom: '0px', width:'83px'}}>{formatCurrency(totals?.subtotal)}</td>
            </tr>
            {Number(totals?.discount || 0) > 0 && (
              <tr>
                <td style={{borderBottom: '0px', borderRight: '0px', color: '#16a34a' }}>
                  Discount{totals?.discountType === 'percentage' && totals?.discountRate ? ` (${totals.discountRate}%)` : ''}:
                </td>
                <td style={{borderBottom: '0px', color: '#16a34a'}}>-{formatCurrency(totals?.discount)}</td>
              </tr>
            )}
            <tr>
              <td style={{borderBottom: '0px', borderRight: '0px' }}>Tax:</td>
              <td style={{borderBottom: '0px'}}>{formatCurrency(totals?.tax)}</td>
            </tr>
            <tr>
              <td style={{borderBottom: '0px', borderRight: '0px' }}>Round Off:</td>
              <td style={{borderBottom: '0px' }}>{formatCurrency(totals?.roundOff)}</td>
            </tr>
            <tr className="total-row">
              <td style={{borderRight: '0px' }}><strong>Total:</strong></td>
              <td><strong>{formatCurrency(totals?.total)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="amount-in-words">
        <strong>Amount in Words:</strong> {amountInWords}
      </div>

      <div className="invoice-footer" style={{ display: 'flex', marginTop: 30 }}>
        <div className="left-col" style={{ flex: '0 0 54.5%', borderLeft: '0px', padding: '8px 12px 0 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 180 }}>
          <div>
            <h3 style={{ marginTop: 0, marginBottom: 6, color: '#333' }}>Account Details</h3>
            {bankDetails && (
              <>
                <p><strong>Bank Name:</strong> {bankDetails.bankName}</p>
                <p><strong>Account Name:</strong> {bankDetails.accountName}</p>
                <p><strong>Account No.:</strong> {bankDetails.accountNumber}</p>
                <p><strong>IFSC Code:</strong> {bankDetails.ifscCode}</p>
                <p><strong>Branch:</strong> {bankDetails.branch}</p>
              </>
            )}
          </div>
          {/* Made with block */}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#666' }}>Made with</span>
            <img src="/spatium-logo.png" alt="Ledgerly Logo" style={{ width: 80, height: 25, objectFit: 'contain' }} />
            <span style={{ fontSize: 10, color: '#666' }}>A product of Spatium Offices</span>
          </div>
        </div>
        <div className="bank-details" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'flex-end', minHeight: 160 }}>
          <h3 style={{ marginBottom: 6, color: '#333' }}>Authorized Signatory</h3>
          <img src={signatureImage} alt="Signature" style={{ maxWidth: '120px', height: 'auto', margin: '16px 0 0 0' }} />
          <div style={{ borderTop: '1.5px solid #bbb', width: '80%', marginTop: '32px', marginBottom: '8px' }}></div>
          <span style={{ fontSize: 13, color: '#888' }}>(Signature)</span>
        </div>
      </div>
    </div>
  );
}

export default QuotePreview;


